import React, { useState } from 'react';
import { CircularProgress } from '@mui/material';
import { Delete as DeleteIcon, Check as CheckIcon } from '@mui/icons-material';
import { useParams } from 'react-router-dom';
import { useAuth0 } from '@auth0/auth0-react';
import type { GmailAccountDTO } from '../../../types/gmail';
import { gmailApi } from '../../../lib/api/gmail';

interface GmailAccountsListProps {
  accounts: GmailAccountDTO[];
  loading: boolean;
  onSetDefault: (accountId: string) => Promise<void>;
  onDisconnect: (accountId: string) => void;
}

export function GmailAccountsList({
  accounts,
  loading,
  onSetDefault,
  onDisconnect
}: GmailAccountsListProps) {
  const { orgId } = useParams<{ orgId: string }>();
  const { getAccessTokenSilently } = useAuth0();
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [selectedAccount, setSelectedAccount] = useState<GmailAccountDTO | null>(null);
  const [processing, setProcessing] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Sort accounts to ensure default is first
  const sortedAccounts = [...accounts].sort((a, b) => {
    if (a.isDefault === b.isDefault) {
      // If both are default or both are not default, sort by email
      return a.email.localeCompare(b.email);
    }
    // Put default account first
    return a.isDefault ? -1 : 1;
  });

  const handleDeleteClick = (account: GmailAccountDTO) => {
    setSelectedAccount(account);
    setDeleteDialogOpen(true);
  };

  const handleDeleteConfirm = async () => {
    if (!selectedAccount || !orgId) return;

    try {
      setProcessing(true);
      setError(null);
      const token = await getAccessTokenSilently();
      await gmailApi.disconnectAccount(orgId, selectedAccount.id, token);
      onDisconnect(selectedAccount.id);
      setDeleteDialogOpen(false);
    } catch (error: any) {
      console.error('Failed to disconnect account:', error);
      // Handle specific error responses
      if (error?.response?.data?.error) {
        setError(error.response.data.error.error || 'Failed to disconnect account');
      } else if (error?.message) {
        setError(error.message);
      } else {
        setError('Failed to disconnect Gmail account. Please try again.');
      }
    } finally {
      setProcessing(false);
      setSelectedAccount(null);
    }
  };

  const handleSetDefault = async (account: GmailAccountDTO) => {
    if (account.isDefault) return;
    
    try {
      setError(null);
      await onSetDefault(account.id);
    } catch (error: any) {
      console.error('Failed to set default account:', error);
      // Handle specific error responses
      if (error?.response?.data?.error) {
        setError(error.response.data.error.error || 'Failed to set default account');
      } else if (error?.message) {
        setError(error.message);
      } else {
        setError('Failed to set default account. Please try again.');
      }
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center p-8">
        <div className="flex flex-col items-center space-y-4">
          <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-indigo-500" />
          <div className="text-gray-400">Loading accounts...</div>
        </div>
      </div>
    );
  }

  if (accounts.length === 0) {
    return (
      <p className="text-center text-gray-400">
        No Gmail accounts connected yet.
      </p>
    );
  }

  return (
    <>
      {error && (
        <div className="mb-4 p-4 bg-red-900/50 border border-red-500/50 rounded-md text-red-200">
          {error}
          <button 
            onClick={() => setError(null)}
            className="float-right text-red-200 hover:text-red-100"
          >
            ×
          </button>
        </div>
      )}
      
      <div className="bg-gray-900 rounded-lg overflow-hidden">
        <table className="w-full divide-y divide-gray-700">
          <thead>
            <tr className="bg-gray-800/50">
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-300 uppercase tracking-wider">Email</th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-300 uppercase tracking-wider">Status</th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-300 uppercase tracking-wider">Connected On</th>
              <th className="px-6 py-3 text-right text-xs font-medium text-gray-300 uppercase tracking-wider">Actions</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-gray-700">
            {sortedAccounts.map((account) => (
              <tr key={account.id} className="hover:bg-gray-800/50">
                <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-200">
                  {account.email}
                </td>
                <td className="px-6 py-4 whitespace-nowrap text-sm">
                  <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium
                    ${account.isDefault 
                      ? 'bg-indigo-900/50 text-indigo-200 border border-indigo-500/50' 
                      : 'bg-gray-900/50 text-gray-200 border border-gray-500/50'}`}
                  >
                    {account.isDefault ? 'Default' : 'Connected'}
                  </span>
                </td>
                <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-300">
                  {new Date(account.createdAt).toLocaleDateString()}
                </td>
                <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-300 text-right">
                  <div className="flex items-center justify-end gap-3">
                    {!account.isDefault && (
                      <button
                        onClick={() => handleSetDefault(account)}
                        className="px-3 py-1.5 bg-indigo-600 text-white rounded-md hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:ring-offset-2 focus:ring-offset-gray-800 transition-colors text-sm font-medium"
                        title="Set as default"
                      >
                        Make Default
                      </button>
                    )}
                    <button
                      onClick={() => handleDeleteClick(account)}
                      className="text-gray-400 hover:text-red-400 p-1 rounded-full hover:bg-red-900/20 transition-colors"
                      title="Disconnect account"
                    >
                      <DeleteIcon className="w-5 h-5" />
                    </button>
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {deleteDialogOpen && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
          <div className="bg-gray-800 rounded-lg shadow-xl max-w-md w-full mx-4">
            <div className="p-6">
              <h3 className="text-lg font-medium text-gray-100 mb-4">
                Disconnect Gmail Account?
              </h3>
              
              {error && (
                <div className="mb-4 p-4 bg-red-900/50 border border-red-500/50 rounded-md text-red-200">
                  {error}
                </div>
              )}

              <p className="text-gray-300 mb-4">
                Are you sure you want to disconnect {selectedAccount?.email}?
              </p>
              
              {selectedAccount?.isDefault && (
                <p className="text-red-300 mb-4">
                  Warning: This is your default account. Another account will be set as default if available.
                </p>
              )}

              <div className="flex justify-end space-x-4">
                <button
                  onClick={() => setDeleteDialogOpen(false)}
                  disabled={processing}
                  className="px-4 py-2 text-gray-300 hover:text-gray-100 transition-colors"
                >
                  Cancel
                </button>
                <button
                  onClick={handleDeleteConfirm}
                  disabled={processing}
                  className="px-4 py-2 bg-red-600 text-white rounded-md hover:bg-red-700 focus:outline-none focus:ring-2 focus:ring-red-500 focus:ring-offset-2 focus:ring-offset-gray-800 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                >
                  {processing ? (
                    <div className="flex items-center">
                      <div className="animate-spin rounded-full h-4 w-4 border-2 border-white/20 border-t-white mr-2" />
                      Disconnecting...
                    </div>
                  ) : (
                    'Disconnect'
                  )}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </>
  );
} 